import { useEffect, useRef, useState } from 'react';
import { limitationTemplateFor, renderLimitationTemplate, solarTemplateOffered, type LimitationScope, type LimitationTemplateId } from '../../domain/authority/ny/limitationTemplates';
import { canCarryLimitedPortions, PORTION_REASON_ORDER, UNABLE_REASON_ORDER, unableReasonNeedsNote, type ItemStatus, type LimitedPortion, type UnableReason } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { unableReasonLabel } from '../../i18n/labels';
import { Button, Field, Icon, InlineMessage, SectionEyebrow, SegmentedControl, SheetShell, TextInput } from '../../ui/components';

// Quick Reason Sheet for Unable to Inspect (task book §15): a light bottom sheet, one reason (required)
// from the closed list, one optional sentence. Never the big form. Cancel leaves the item untouched.
//
// NY F1 Round 3 — ONE toggle at the top (frozen Plan §12.2; Owner decision OD-5 = A):
//   whole   the whole item could not be inspected → the row becomes Unable to Inspect (the unchanged R2 path)
//   part    part of it could not be seen → ONE limited portion is recorded on the row; the row keeps the
//           result already recorded for the observed part; no new row, no task
// Owner decision OD-7 = C: `obstructed` needs no typing — the sheet shows the frozen NY wording the report
// will state and a typed note replaces it; `other` cannot be confirmed without the inspector's own words.
// Every other closed code keeps its optional note.
//
// NY F1 R3 narrow repair:
//   F-R3-01  `obstructed` states the GENERIC obstruction wording by default. On a partially observed roof
//            row the sheet additionally asks what obstructs the part — "stored items, coverings or
//            finishes" (pre-selected) or "solar panels / solar array" — and only that explicit choice
//            switches to the frozen §5 solar wording. Changing the reason or the scope drops the choice.
//   F-R3-02  "part of it" is offered only on a row that keeps (or can still receive) an observed result;
//            on a whole-row Unable / N/A the option is disabled and the sheet says why.
//   F-R3-03  the wording that will print, or for `other` the required field and its message, is scrolled
//            into view the moment the reason is chosen (the sheet shell keeps the sticky footer clear);
//            the fields follow the wording, so nothing the inspector must see sits below the footer at
//            the moment the confirm becomes enabled.
export function UnableReasonSheet(props: {
  itemTitle: string;
  rowTemplateId: string | null;
  itemStatus: ItemStatus;
  existingPortions: number;
  initialScope?: LimitationScope;
  initialReason: UnableReason | null;
  initialNote: string;
  // Editing an already recorded portion (partial scope).
  initialPortion?: LimitedPortion | null;
  onConfirm: (reason: UnableReason, note: string) => void;
  onConfirmPortion: (portion: LimitedPortion) => void;
  onClose: () => void;
}) {
  const { t, tp, locale } = useT();
  const editing = props.initialPortion ?? null;
  // F-R3-02: only a row that carries (or can still receive) an observed result has a part not observed.
  const partialAllowed = canCarryLimitedPortions(props.itemStatus);
  const [scope, setScope] = useState<LimitationScope>(partialAllowed && props.initialScope === 'partial' ? 'partial' : 'whole');
  const [reason, setReason] = useState<UnableReason | null>(editing ? editing.reasonCode : props.initialReason);
  const [extent, setExtent] = useState(editing ? editing.extent : '');
  const [note, setNote] = useState(editing ? editing.note : props.initialNote);
  // F-R3-01: the solar wording is the inspector's affirmative choice, never a default — it starts
  // selected only when the portion being edited was recorded with it.
  const [solar, setSolar] = useState(editing !== null && editing.templateId === 'solarArrayRoof');
  const reasons = scope === 'partial' ? PORTION_REASON_ORDER : UNABLE_REASON_ORDER;
  const needsNote = unableReasonNeedsNote(reason);
  const noteBlank = note.trim().length === 0;
  const solarOffered = reason === 'obstructed' && solarTemplateOffered(props.rowTemplateId, scope);
  const templateId: LimitationTemplateId | null = reason === null ? null : solarOffered && solar ? 'solarArrayRoof' : limitationTemplateFor(reason);
  const reportReason = reason && !needsNote && noteBlank && templateId ? renderLimitationTemplate(templateId, { locale, component: props.itemTitle, extent, scope, method: null }) : null;
  const canConfirm = reason !== null && !(needsNote && noteBlank) && (scope === 'whole' || partialAllowed);

  // F-R3-03: reveal what the choice means as soon as it is made.
  const consequenceRef = useRef<HTMLDivElement | null>(null);
  const noteBlockRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const target = needsNote ? noteBlockRef.current : consequenceRef.current;
    if (target && typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'nearest' });
  }, [reason, scope, templateId, needsNote]);

  function chooseScope(next: LimitationScope) {
    if (next === 'partial' && !partialAllowed) return;
    setScope(next);
    if (next === 'partial' && reason === 'notInstalled') setReason(null);
    setSolar(false);
  }

  function chooseReason(code: UnableReason) {
    setReason(code);
    setSolar(false);
  }

  function confirm() {
    if (reason === null || !canConfirm) return;
    if (scope === 'partial') props.onConfirmPortion({ reasonCode: reason, extent: extent.trim(), note: note.trim(), templateId });
    else props.onConfirm(reason, note);
  }

  const showConsequence = solarOffered || reportReason !== null;

  return (
    <SheetShell
      title={scope === 'partial' ? t('unable.portionTitle') : t('result.unableToInspect')}
      subtitle={props.itemTitle}
      onClose={props.onClose}
      layer="over"
      testId="unable-reason-sheet"
      footer={
        <>
          <Button onClick={confirm} disabled={!canConfirm} testId="unable-confirm">
            {scope === 'partial' ? t('unable.confirmPortion') : t('unable.confirm')}
          </Button>
          <Button variant="text" onClick={props.onClose} testId="unable-cancel">
            {t('shell.cancel')}
          </Button>
        </>
      }
    >
      <div className="unable-scope">
        <SegmentedControl
          variant="pill"
          block
          ariaLabel={t('result.unableToInspect')}
          value={scope}
          onChange={chooseScope}
          options={[
            { key: 'whole', label: t('unable.scopeWhole'), testId: 'unable-scope-whole' },
            { key: 'partial', label: t('unable.scopePartial'), disabled: !partialAllowed, testId: 'unable-scope-partial' }
          ]}
          testId="unable-scope"
        />
      </div>
      <p className="card-note">{scope === 'partial' ? t('unable.partialBody') : t('unable.body')}</p>
      {!partialAllowed ? <InlineMessage kind="info" text={t('unable.partialUnavailable')} /> : null}
      {scope === 'partial' && props.itemStatus === 'notChecked' ? <InlineMessage kind="info" text={t('unable.partialNeedsResult')} /> : null}
      {scope === 'whole' && props.existingPortions > 0 ? <InlineMessage kind="info" text={tp('unable.replacesPortions', props.existingPortions)} /> : null}
      <div className="option-list" role="listbox" data-testid="unable-reasons">
        {reasons.map((code) => {
          const selected = reason === code;
          return (
            <button key={code} type="button" role="option" aria-selected={selected} className={`option-row ${selected ? 'option-row-selected' : ''}`} onClick={() => chooseReason(code)} data-testid={`reason-${code}`}>
              <span>{unableReasonLabel(t, code)}</span>
              {selected ? <Icon name="check" /> : null}
            </button>
          );
        })}
      </div>
      {showConsequence ? (
        <div ref={consequenceRef} className="unable-consequence" data-testid="unable-consequence">
          {solarOffered ? (
            <div className="unable-obstruction">
              <SectionEyebrow>{t('unable.obstruction')}</SectionEyebrow>
              <div className="option-list" role="radiogroup" aria-label={t('unable.obstruction')} data-testid="unable-obstruction">
                <button type="button" role="radio" aria-checked={!solar} className={`option-row ${!solar ? 'option-row-selected' : ''}`} onClick={() => setSolar(false)} data-testid="unable-obstruction-generic">
                  <span>{t('unable.obstructionGeneric')}</span>
                  {!solar ? <Icon name="check" /> : null}
                </button>
                <button type="button" role="radio" aria-checked={solar} className={`option-row ${solar ? 'option-row-selected' : ''}`} onClick={() => setSolar(true)} data-testid="unable-obstruction-solar">
                  <span>{t('unable.obstructionSolar')}</span>
                  {solar ? <Icon name="check" /> : null}
                </button>
              </div>
            </div>
          ) : null}
          {reportReason ? (
            <p className="unable-report-reason" data-testid="unable-report-reason" data-template={templateId ?? ''}>
              <strong>{t('unable.reportReason')}</strong>
              {reportReason}
            </p>
          ) : null}
        </div>
      ) : null}
      {scope === 'partial' ? (
        <Field label={t('unable.extent')} htmlFor="unable-extent" trailing={<span className="field-required">{t('common.optional')}</span>}>
          <TextInput id="unable-extent" value={extent} onChange={setExtent} placeholder={t('unable.extentPlaceholder')} testId="unable-extent" />
        </Field>
      ) : null}
      <div ref={noteBlockRef} className="unable-note-block" data-testid="unable-note-block">
        <Field label={needsNote ? t('unable.reasonWords') : t('unable.note')} htmlFor="unable-note" trailing={<span className="field-required">{needsNote ? t('common.required') : t('common.optional')}</span>}>
          <TextInput id="unable-note" value={note} onChange={setNote} placeholder={needsNote ? t('item.unableNotePlaceholder') : t('unable.notePlaceholder')} testId="unable-note" />
        </Field>
        {needsNote && noteBlank ? <InlineMessage kind="err" text={t('unable.otherNeedsReason')} /> : null}
      </div>
    </SheetShell>
  );
}
